import React, { useState } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    CardMedia,
    Button,
} from "@mui/material";
import { FileText, Clock } from "lucide-react";
import { motion } from "framer-motion";
import DefaultCourseImage from "../../../../Assets/Images/CourseCardAI.png";

interface CourseCardProps {
    title: string;
    image?: string;
    description?: string;
    category?: string;
    subjects?: number; // Renamed from topics
    duration?: string | number;
    status?: string | number;
    onClick?: () => void;
    actionLabel?: string;
}

const CourseCard: React.FC<CourseCardProps> = ({
    title,
    image,
    description,
    category,
    subjects = 0,
    duration,
    status,
    onClick,
    actionLabel = "View Topics"
}) => {
    const [imgError, setImgError] = useState(false);

    return (
        <motion.div
            layout
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            style={{ height: "100%" }}
        >
            <Card 
                onClick={onClick}
                sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "16px",
                    overflow: "hidden",
                    border: "1px solid rgba(0,0,0,0.05)",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    bgcolor: "#fff",
                    cursor: "pointer",
                    "&:hover": {
                        boxShadow: "0 20px 40px rgba(0, 102, 255, 0.12)",
                        transform: "translateY(-8px)",
                        "& .course-image": {
                            transform: "scale(1.08)"
                        }
                    }
                }}
            >
                <Box sx={{ 
                    height: 180, 
                    overflow: "hidden", 
                    position: "relative",
                    bgcolor: "rgba(0,0,0,0.02)"
                }}>
                    <CardMedia
                        component="img"
                        className="course-image"
                        image={(!image || imgError) ? DefaultCourseImage : image}
                        alt={title}
                        onError={() => setImgError(true)}
                        sx={{ 
                            height: "100%",
                            width: "100%",
                            objectFit: "cover",
                            transition: "transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)"
                        }}
                    />
                    
                    {/* Default Tag shown if image fails to load or is not provided */}
                    {(imgError || !image) && (
                        <Box sx={{
                            position: "absolute",
                            bottom: 12,
                            right: 12,
                            px: 1.2,
                            py: 0.6,
                            borderRadius: "12px",
                            bgcolor: "rgba(255, 255, 255, 0.4)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)", 
                            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                            zIndex: 2,
                            border: "1px solid rgba(0,0,0,0.05)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}>
                            <Typography sx={{ 
                                fontSize: "10px", 
                                fontWeight: 400, 
                                color: "#e7e7e7ff",
                                textTransform: "none",
                                letterSpacing: "1px",
                                lineHeight: 1
                            }}>
                                Default
                            </Typography>
                        </Box>
                    )}

                    {status !== undefined && (
                        <Box sx={{
                            position: "absolute",
                            top: 12,
                            left: 12,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: "20px" ,
                            bgcolor: "rgba(255, 255, 255, 0.9)",
                            backdropFilter: "blur(4px)",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            zIndex: 2,
                        }}>
                            <Typography sx={{ 
                                fontSize: "10px", 
                                fontWeight: 400, 
                                color: "#0066FF", 
                                textTransform: "uppercase",
                                letterSpacing: "0.5px"
                            }}>
                                {status === 1 || status === "Active" ? "Active" : 
                                 status === 0 || status === "Inactive" ? "Inactive" : status}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <CardContent sx={{ p: 2.5, display: "flex", flexDirection: "column", flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontSize: "18px", fontWeight: 600, color: "#1e293b", mb: 1, lineHeight: 1.3, minHeight: "2.6em", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        {title}
                    </Typography>
                    
                    <Typography variant="body2" sx={{ color: "#64748b", mb: 2.5, fontSize: "13.5px", lineHeight: 1.6, minHeight: "3.2em", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                        {description || "Master new concepts through specialized lessons and expert-led tutorials."}
                    </Typography>

                    <Box sx={{ mt: "auto" }}>
                        <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#94a3b8", mb: 1.5, textTransform: "none" }}>
                            {category || "Course Program"}
                        </Typography>

                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <FileText size={15} color="#64748b" />
                                <Typography sx={{ fontWeight: 400, fontSize: "15px", color: "#64748b" }}>
                                    {subjects} {subjects === 1 ? "Subject" : "Subjects"}
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                                <Clock size={15} color="#64748b" />
                                <Typography sx={{ fontWeight: 400, fontSize: "15px", color: "#64748b" }}>
                                    {duration || "N/A"}
                                </Typography>
                            </Box>
                        </Box>

                        <Button
                            fullWidth
                            variant="contained"
                            sx={{
                                borderRadius: "8px",
                                py: 1.2,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "14px",
                                bgcolor: "#0066FF",
                                boxShadow: "none",
                                "&:hover": {
                                    bgcolor: "#0052cc",
                                    boxShadow: "0 4px 12px rgba(0, 102, 255, 0.2)"
                                },
                                transition: "all 0.2s",
                                color: "#fff"
                            }}
                        >
                            {actionLabel}
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </motion.div>
    );
};

export default CourseCard;
